import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { getConteudoChave, CONTEUDO_CHAVES } from '@/api/adminApi'
import { cadastrarNewsletter } from '@/api/usuariosApi'
import { useToast } from '@/contexts/ToastContext'
import { useConteudoFlag, useRequireConteudoFlag } from '@/utils/featureFlags'
import { useAgendamentoFlags } from '@/hooks/useAgendamentoFlags'
import styles from './ServicosPage.module.css'

export type ServicoItem = { nome: string; categoria: string }

function parseServicos(raw: string | null): { intro: string; items: ServicoItem[] } {
  if (!raw?.trim()) return { intro: '', items: [] }
  try {
    const j = JSON.parse(raw) as { intro?: string; lista?: string; items?: Array<{ nome?: string; categoria?: string }> }
    if (Array.isArray(j.items)) {
      const items = j.items
        .filter((x): x is { nome?: string; categoria?: string } => x != null && typeof x === 'object')
        .map((x) => ({ nome: String(x.nome ?? '').trim(), categoria: String(x.categoria ?? '').trim() }))
        .filter((x) => x.nome)
      return { intro: j.intro ?? '', items }
    }
    const lista = (j.lista ?? '').trim()
    const items: ServicoItem[] = lista
      ? lista.split(/\n/).map((s) => ({ nome: s.trim(), categoria: '' })).filter((x) => x.nome)
      : []
    return { intro: j.intro ?? '', items }
  } catch {
    return { intro: raw, items: [] }
  }
}

type CategoriaItem = { nome: string }

function parseCategorias(raw: string | null): CategoriaItem[] {
  if (!raw?.trim()) return []
  try {
    const arr = JSON.parse(raw) as unknown
    if (!Array.isArray(arr)) return []
    return arr
      .filter((x): x is { nome?: string } => x != null && typeof x === 'object')
      .map((x) => ({ nome: String(x.nome ?? '').trim() }))
      .filter((x) => x.nome)
  } catch {
    return []
  }
}

export function ServicosPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { loading: featureLoading } = useRequireConteudoFlag(CONTEUDO_CHAVES.menu_show_servicos)
  const { flags: agFlags } = useAgendamentoFlags()
  const { enabled: vipEnabled } = useConteudoFlag(CONTEUDO_CHAVES.vip_enabled)
  const servicosSectionRef = useRef<HTMLDivElement>(null)
  const [intro, setIntro] = useState('')
  const [items, setItems] = useState<ServicoItem[]>([])
  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [enviado, setEnviado] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [loading, setLoading] = useState(true)
  const [categorias, setCategorias] = useState<CategoriaItem[]>([])
  const [categoriaFiltro, setCategoriaFiltro] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      getConteudoChave(CONTEUDO_CHAVES.servicos_intro),
      getConteudoChave(CONTEUDO_CHAVES.categorias).catch(() => null),
    ]).then(([introVal, catVal]) => {
      if (cancelled) return
      const { intro: i, items: it } = parseServicos(introVal ?? null)
      setIntro(i)
      setItems(it)
      const cats = parseCategorias(catVal ?? null)
      setCategorias(cats)
      if (!cancelled && cats.length === 0) setCategoriaFiltro(null)
    }).catch(() => {
      if (!cancelled) setIntro(''); setItems([]); setCategorias([])
    }).finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const servicosFiltrados =
    categoriaFiltro != null && categoriaFiltro !== ''
      ? items.filter((s) => s.categoria === categoriaFiltro)
      : items

  const handleCategoriaClick = (nomeCategoria: string) => {
    setCategoriaFiltro((atual) => (atual === nomeCategoria ? null : nomeCategoria))
    setTimeout(() => servicosSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!nome.trim() || !email.trim()) return
    setEnviando(true)
    try {
      await cadastrarNewsletter(nome, email)
      setEnviado(true)
      toast('Obrigado! E-mail registado.', 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao registar e-mail', 'error')
    } finally {
      setEnviando(false)
    }
  }

  if (featureLoading) return null

  const abrirAgendamento = () => {
    if (agFlags.servicosMarcacao) navigate('/agendamento')
  }

  return (
    <main className={`${styles.page} animate-slide-up`}>
      <button
        type="button"
        className={styles.backLink}
        onClick={() => navigate(-1)}
      >
        ← Voltar
      </button>

      <div className={styles.wrapper}>
        <h1 className={styles.heading}>Nossos serviços</h1>
        {loading ? (
          <p className={styles.intro}>A carregar…</p>
        ) : (
          <>
            {intro.trim() && <p className={styles.intro}>{intro}</p>}
            {!intro.trim() && <p className={styles.intro}>Conheça nossas opções de cuidados e tratamentos. Escolha o serviço que combina com você e agende com facilidade.</p>}

            <span className={styles.sectionLabel}>Serviços</span>
            {categoriaFiltro && (
              <p className={styles.filtroAtivo}>
                A mostrar: <strong>{categoriaFiltro}</strong>
                <button type="button" className={styles.limparFiltro} onClick={() => setCategoriaFiltro(null)}>
                  Ver todos
                </button>
              </p>
            )}
            <div ref={servicosSectionRef} className={styles.gridServicos}>
              {servicosFiltrados.length > 0
                ? servicosFiltrados.map((s, i) =>
                    agFlags.servicosMarcacao ? (
                      <button
                        key={i}
                        type="button"
                        className={styles.servicoCard}
                        onClick={abrirAgendamento}
                      >
                        {s.nome}
                      </button>
                    ) : (
                      <div key={i} className={`${styles.servicoCard} ${styles.servicoCardStatic}`}>
                        {s.nome}
                      </div>
                    )
                  )
                : items.length === 0
                  ? ['Unhas', 'Massagem', 'Spa', 'Cabelo', 'Cílios'].map((s) =>
                      agFlags.servicosMarcacao ? (
                        <button key={s} type="button" className={styles.servicoCard} onClick={abrirAgendamento}>
                          {s}
                        </button>
                      ) : (
                        <div key={s} className={`${styles.servicoCard} ${styles.servicoCardStatic}`}>
                          {s}
                        </div>
                      )
                    )
                  : (
                      <p className={styles.nenhumServico}>Nenhum serviço nesta categoria. Clique noutra categoria ou &quot;Ver todos&quot;.</p>
                    )}
            </div>
          </>
        )}

        <span className={styles.sectionLabel}>Cadastre-se e ganhe novidades</span>
        <div className={styles.formBlock}>
          {enviado ? (
            <p style={{ color: 'var(--success)', fontWeight: 600 }}>
              Obrigado! Em breve você receberá nossas novidades.
            </p>
          ) : (
            <form className={styles.formRow} onSubmit={handleSubmit}>
              <input
                type="text"
                className={styles.input}
                placeholder="Seu nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
              />
              <input
                type="email"
                className={styles.input}
                placeholder="Seu e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button type="submit" className={styles.btnEnviar} disabled={enviando}>
                {enviando ? 'A enviar…' : 'Quero receber'}
              </button>
            </form>
          )}
        </div>

        <span className={styles.sectionLabel}>Onde estamos</span>
        <div className={styles.mapBlock}>
          <button
            type="button"
            className={styles.mapLink}
            onClick={() => navigate('/mapa')}
          >
            📍 Ver mapa / Como chegar
          </button>
        </div>

        {categorias.length > 0 && (
          <>
            <span className={styles.iconsLabel}>Categorias</span>
            <p className={styles.categoriasHint}>Toque numa categoria para ver os serviços dessa área.</p>
            <div className={styles.iconsGrid}>
              {categorias.map((cat, i) => (
                <button
                  key={i}
                  type="button"
                  className={`${styles.iconCard} ${categoriaFiltro === cat.nome ? styles.iconCardActive : ''}`}
                  onClick={() => handleCategoriaClick(cat.nome)}
                  aria-label={cat.nome || `Categoria ${i + 1}`}
                  aria-pressed={categoriaFiltro === cat.nome}
                >
                  <span className={styles.iconCardNome}>{cat.nome}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {vipEnabled && (
          <button
            type="button"
            className={styles.linkVip}
            onClick={() => navigate('/vip')}
          >
            Conheça o programa V.I.P →
          </button>
        )}
      </div>
    </main>
  )
}
